import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que «Estados de la orden» (O-13, #145) le pide a la API, con la forma de
// `marcas/datos.ts`: una clave de caché con el taller adentro, una consulta y una
// función por escritura que devuelve el `ProblemDetails` o nada.

export type Estado = components['schemas']['OrderStatusSettingsItem'];
export type Etapa = components['schemas']['OrderStatusCategory'];

/** Las cinco etapas, en el orden en que avanza una orden: el de la API. */
export const ETAPAS: readonly Etapa[] = ['open', 'inProgress', 'completed', 'closed', 'cancelled'];

/// El taller de la pestaña (#205), en toda clave de caché: regla 5 de multitenancy, en
/// el navegador. Igual que en marcas.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  estados: (taller: string) => ['estados-de-orden', taller] as const,
  // El combo «Cambiar estado» y la bandeja de órdenes: ver `ordenes/datos.ts`.
  ordenes: (taller: string) => ['ordenes', taller, 'estados'] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

export function useEstadosDeOrden() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.estados(taller),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/order-statuses/settings');
      if (error || !data) throw new Error('No se pudieron leer los estados de la orden');
      return data;
    },
  });
}

export interface ValoresEstado {
  name: string;
  category: Etapa;
  /** `#RRGGBB`, o vacío para «sin color». */
  color: string;
  isFinal: boolean;
}

/**
 * Las escrituras. Al terminar se invalida la lista **y el combo de las órdenes**:
 * un estado renombrado o desactivado tiene que verse igual en la ficha.
 *
 * También cuando la API dice que no: un `conflict.cambio_simultaneo` quiere decir
 * que la versión en caché ya es vieja, y sin refrescarla volver a abrir la
 * ventana chocaría otra vez.
 */
export function useCambiosEstados() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function tras<T extends { error?: unknown }>(peticion: Promise<T>): Promise<ProblemaApi | undefined> {
    const { error } = await peticion;
    await Promise.all([
      cache.invalidateQueries({ queryKey: claves.estados(taller) }),
      cache.invalidateQueries({ queryKey: claves.ordenes(taller) }),
    ]);
    return error ? (error as ProblemaApi) : undefined;
  }

  // Sin color: el campo no va. El contrato no tiene un «vacío» distinto de omitirlo.
  const cuerpo = (v: ValoresEstado) => ({
    name: v.name.trim(),
    category: v.category,
    isFinal: v.isFinal,
    ...(v.color && { color: v.color }),
  });

  return {
    crear: (v: ValoresEstado) => tras(api.POST('/api/order-statuses', { body: cuerpo(v) })),

    editar: (id: string, versionActual: number, v: ValoresEstado) =>
      tras(
        api.PUT('/api/order-statuses/{id}', {
          params: { path: { id } },
          body: { version: versionActual, ...cuerpo(v) },
        }),
      ),

    nombrarInicial: (id: string) => tras(api.PUT('/api/order-statuses/{id}/initial', { params: { path: { id } } })),

    cambiarActivacion: (id: string, activo: boolean) =>
      tras(api.PUT('/api/order-statuses/{id}/status', { params: { path: { id } }, body: { isActive: activo } })),

    /** Se manda el orden de todos, activos e inactivos, como pide la API. */
    reordenar: (ids: string[]) => tras(api.PUT('/api/order-statuses/order', { body: { ids } })),
  };
}
